import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box, Typography, Chip, Link, CircularProgress, Button,
  useTheme
} from '@mui/material';
import { Audio, AudioPlayer, NeutralAudioSkin } from '@videojs/react/audio';
import '@videojs/react/audio/neutral-skin.css';
import api from '../utils/api';
import { sanitizedHtml } from '../utils/sanitize';
import TranscriptPanel from './TranscriptPanel';
import SummaryPanel from './SummaryPanel';
import SponsorTranslationCard from './SponsorTranslationCard';

function RecordedPlayerPage() {
  const { program_id } = useParams();
  const [program, setProgram] = useState(null);
  const [loading, setLoading] = useState(true);
  const theme = useTheme();
  const audioRef = useRef(null);
  // 供 TranscriptPanel / SummaryPanel 取用底層 <audio> 元素（Video.js 的 Audio ref 直接指向 HTMLAudioElement）。
  // useCallback 保持參照穩定，避免子元件的 useEffect 因新函式參照頻繁重跑。
  const getPlayer = useCallback(() => audioRef.current || null, []);

  // Video.js audio skin 透過 color-scheme 的 light-dark() 自動切換明暗配色，
  // 主色則用 --media-accent-color 對齊 MUI theme。
  const playerSkinStyle = {
    width: '100%',
    colorScheme: theme.palette.mode,
    '--media-accent-color': theme.palette.primary.main,
    '--media-accent-text-color': theme.palette.primary.contrastText,
  };

  useEffect(() => {
    api.get(`/programs/${program_id}`)
      .then(res => {
        setProgram(res.data);
        setLoading(false);
      })
      .catch(err => {
        console.error('無法取得節目資料:', err);
        setLoading(false);
      });
  }, [program_id]);

  const formatDateOnly = (timeString) => {
    if (!timeString || timeString.length < 8) return '未知日期';
    return `${timeString.slice(0, 4)}/${timeString.slice(4, 6)}/${timeString.slice(6, 8)}`;
  };

  const formatProgramTime = (timeString) => {
    if (!timeString || timeString.length < 12) return '未知時間';
    return `${timeString.slice(0, 4)}/${timeString.slice(4, 6)}/${timeString.slice(6, 8)} ${timeString.slice(8, 10)}:${timeString.slice(10, 12)}`;
  };

  const getDurationText = (start, end) => {
    const s = new Date(`${start.slice(0, 4)}-${start.slice(4, 6)}-${start.slice(6, 8)}T${start.slice(8, 10)}:${start.slice(10, 12)}:${start.slice(12, 14)}+09:00`);
    const e = new Date(`${end.slice(0, 4)}-${end.slice(4, 6)}-${end.slice(6, 8)}T${end.slice(8, 10)}:${end.slice(10, 12)}:${end.slice(12, 14)}+09:00`);
    const diffMin = Math.round((e - s) / 60000);
    const hours = Math.floor(diffMin / 60);
    const mins = diffMin % 60;
    return hours > 0 ? `${hours}小時${mins > 0 ? mins + '分鐘' : ''}` : `${mins}分鐘`;
  };

  useEffect(() => {
    if (program) {
      document.title = `${formatDateOnly(program.start_time)} ${program.title}`;
    }
  }, [program]);

  if (loading) return <CircularProgress />;
  if (!program) return <Typography>找不到節目</Typography>;

  const playUrl = program.file_url;

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        {formatDateOnly(program.start_time)} {program.title}
      </Typography>

      {program.img && (
        <img src={program.img} alt="cover" style={{ maxWidth: '100%', marginBottom: '1rem' }} />
      )}

      <Box sx={{ mb: 2 }}>
        <AudioPlayer>
          <NeutralAudioSkin style={playerSkinStyle}>
            <Audio ref={audioRef} src={playUrl} preload="metadata" />
          </NeutralAudioSkin>
        </AudioPlayer>
      </Box>

      {program.translated ? (
        <>
          <SummaryPanel programId={program_id} getPlayer={getPlayer} />
          <TranscriptPanel programId={program_id} getPlayer={getPlayer} />
        </>
      ) : (
        <SponsorTranslationCard program={program} />
      )}

      <Box sx={{ mt: 2 }}>
        <Typography variant="body2" color="textSecondary">
          電台：{program.station_name || program.station_id}
        </Typography>

        <Typography variant="body2" color="textSecondary">
          時間：{formatProgramTime(program.start_time)} - {formatProgramTime(program.end_time)}
        </Typography>

        <Typography variant="body2" color="textSecondary">
          長度：{getDurationText(program.start_time, program.end_time)}
        </Typography>

        {Array.isArray(program.person) && program.person.length > 0 && (
          <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
            演出者：{program.person.map(p => p.name).join(', ')}
          </Typography>
        )}

        {program.season_name && (
          <Typography variant="body2" color="textSecondary">
            節目：{program.season_name}
          </Typography>
        )}

        {program.summary && (
          <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
            摘要：<span dangerouslySetInnerHTML={sanitizedHtml(program.summary)} />
          </Typography>
        )}

        {program.description && (
          <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
            說明：<span dangerouslySetInnerHTML={sanitizedHtml(program.description)} />
          </Typography>
        )}

        {program.url && (
          <Box sx={{ mt: 1 }}>
            <Link href={program.url} target="_blank" rel="noopener">
              節目官方網站
            </Link>
          </Box>
        )}

        {Array.isArray(program.tag) && program.tag.length > 0 && (
          <Box sx={{ mt: 1 }}>
            {program.tag.map((tag, i) => (
              <Chip
                key={typeof tag === 'string' ? tag : i}
                label={typeof tag === 'string' ? tag : tag.name || JSON.stringify(tag)}
                style={{ marginRight: '5px', marginTop: '5px' }}
              />
            ))}
          </Box>
        )}

        <Box sx={{ mt: 2 }}>
          <Button variant="outlined" href={playUrl} download>
            下載錄音檔
          </Button>
        </Box>
      </Box>
    </Box>
  );
}

export default RecordedPlayerPage;
